// Mock content for BridgeColumbia — Columbia University Chapter of BridgeUSA
// Source: bridgecolumbia.weebly.com

export const BRAND = {
  name: "BridgeColumbia",
  tagline: "The Columbia University Chapter of BridgeUSA",
  subtagline: "A multi-partisan student movement for viewpoint diversity and responsible discourse.",
  email: "bridgecolumbia@columbia.edu",
  instagram: "https://instagram.com/bridge.columbia",
  instagramHandle: "@bridge.columbia",
  gbmFormUrl:
    "https://docs.google.com/forms/d/e/1FAIpQLSfuiRItZhIkUzPOQGyB5iRe9Vak3huJ0xLgJsQCT1gEtdq_Qw/viewform?utm_source=ig&utm_medium=social&utm_content=link_in_bio",
  fellowshipUrl: "https://docs.google.com/forms/d/e/1FAIpQLSc0GxMZ-0pQLZsVYjG4gM13uxUS5Efbo_yebYenxGxcyAXubg/viewform",
  fellowshipComingSoon: false,
  committeeUrl:
    "https://docs.google.com/forms/d/e/1FAIpQLSdyYy2U8F9E8UzA2P3Q7nhJiogRMX1rngERuOgXR1hfuhsOag/viewform",
  founded: "September 2024",
  quote:
    '"We need to restore dialogue on campus, and BridgeUSA offers a unique model to help foster these essential conversations."',
  quoteAttr: "Harvey Pennington, Senior Advisor",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Board", to: "/board" },
  { label: "Events", to: "/events" },
  { label: "Get Involved", to: "/get-involved" },
  { label: "Constitution", to: "/constitution" },
];

export const MISSION = {
  short:
    "BridgeUSA is a multi-partisan student movement that champions viewpoint diversity, responsible discourse, and a solution-oriented political culture.",
  paragraphs: [
    "The purpose of BridgeUSA is to build a community — one in which students from across the ideological spectrum can engage as a group working together to understand, and to bridge, the various perspectives behind the important political and social issues of our time.",
    "At BridgeColumbia, we seek to reduce political polarization within the wider Columbia community, emphasizing the need to respectfully engage with and listen to ideas from across the political spectrum. We tackle the most pressing political challenges of our time — from upcoming elections to global events — through constructive disagreement and honest engagement.",
    "We host speaker events, town hall meetings, and other organized assemblies in the hopes of fostering a collective empathy and cooperation within the Columbia student body that will allow us to approach these issues holistically.",
  ],
};

export const MINDSET_INTRO =
  "The goal of BridgeUSA is not to change minds or create centrists, but to encourage students to engage differing beliefs, experiences, and perspectives. What unites us is not agreement on policies, but a shared mindset that champions understanding, empathy, open-mindedness, and a willingness to engage those who are different from us.";

export const DISCUSSION_NORMS = [
  {
    title: "Listen to listen, not to respond",
    body:
      "Instead of planning how to respond or thinking about what to say next, focus on listening to and understanding the ideas of others before formulating a response.",
  },
  {
    title: "Don’t interrupt or have side conversations",
    body:
      "We are all here with the best intentions. By not interrupting and not talking on the side, we ensure that all voices are heard and respected.",
  },
  {
    title: "Address the statement, not the person",
    body:
      "When responding to arguments and comments, we address what was said — not the worth, morality, or goodness of the person who said it. The discussion is about ideas.",
  },
  {
    title: "Participants represent only themselves",
    body:
      "Though we may reflect the views of our communities, within the dialogue no one is expected to represent organizations or groups. Identity experiences are welcome and should be treated with respect.",
  },
];

export const TIMELINE = [
  {
    when: "Fall 2023",
    title: "A Campus in Crisis",
    body:
      "Columbia’s campus made national headlines as protests raged in response to Israel’s war in Gaza. Students reported high levels of division and confusion as protesters and counter-protesters alike felt compelled to self-censor.",
  },
  {
    when: "September 2024",
    title: "BridgeColumbia Launches",
    body:
      "BridgeColumbia officially launches at Columbia. Founders Harvey Pennington, TJ Gill, and Nick Baum partner with BridgeUSA to bring a multi-partisan dialogue coalition to campus, joined by faculty mentor Peter T. Coleman, a renowned social psychologist and expert in constructive conflict resolution.",
  },
  {
    when: "Today",
    title: "Bridging the Divide",
    body:
      "BridgeColumbia continues to champion the spirit of open dialogue. We host speakers, open forums, and club collaborations in an effort to bring the campus together, especially during periods of heightened political tension.",
  },
];

export const EVENT_TYPES = [
  {
    title: "Discussions",
    body:
      "Moderated small-group conversations guided by our four discussion norms. Students from across the ideological spectrum work together to understand the various sides of an issue, not to win.",
  },
  {
    title: "Interactive Forums",
    body:
      "Jubilee-inspired formats — political spectrums, middle grounds, and other structured exercises that get participants moving, talking, and rethinking their assumptions in real time.",
  },
  {
    title: "Socials",
    body:
      "Low-stakes gatherings that build the trust and friendships that make hard conversations possible. From coffee chats to game nights, socials are where the coalition comes together off-hours.",
  },
  {
    title: "Expert Panels",
    body:
      "Scholars, practitioners, and journalists in conversation with each other on complex topics. Panels model rigorous, good-faith engagement across disciplinary and ideological lines.",
  },
  {
    title: "Debates",
    body:
      "We co-host debates with other Columbia student groups so members can hear nuanced perspectives from ideologically diverse peers who share expertise but disagree on the substance.",
  },
  {
    title: "Speakers",
    body:
      "BridgeColumbia invites speakers with unique experiences and viewpoints to campus — experts in current events, advocates for constructive discourse, and figures who challenge our assumptions.",
  },
  {
    title: "Networking Events",
    body:
      "Alumni mixers, faculty dinners, and cross-organization meet-ups that connect Bridge students with mentors, employers, and the broader civic-dialogue community.",
  },
];

export const BOARD = [
  { name: "Giada Mercer", role: "President", email: "gm3051@columbia.edu", school: "Columbia University" },
  { name: "Christina Ma", role: "Vice President", email: "chm2153@barnard.edu", school: "Barnard College" },
  { name: "Jessica Nwosu", role: "Director of Strategy", email: "jnn2120@columbia.edu", school: "Columbia College" },
  { name: "Nick Baum", role: "Director of Outreach", email: "nb3183@columbia.edu", school: "General Studies" },
  { name: "Ryan Crawford", role: "Director of Communications", email: "rpc2135@columbia.edu", school: "Columbia College" },
  { name: "Sebastiana Hobey", role: "Director of Grants and Partnerships", email: "slh2212@barnard.edu", school: "Barnard College" },
  { name: "Nyrah Siddiqui", role: "Director of Creative Marketing", email: "ns3673@barnard.edu", school: "Barnard College" },
  { name: "Roxy Borletti", role: "Director of Fundraising and Development", email: "rb3725@barnard.edu", school: "Barnard College" },
  { name: "Noah Edelman", role: "Director of STEM Programming", email: "nle2113@columbia.edu", school: "Columbia College" },
  { name: "Iliana Weisberg", role: "Secretary", email: "iw2276@barnard.edu", school: "Barnard College" },
  { name: "Harvey Pennington", role: "Senior Advisor", email: "hp2666@columbia.edu", school: "General Studies", founder: true },
  { name: "TJ Gill", role: "Senior Advisor", email: "gg2727@columbia.edu", school: "Columbia Law School", founder: true },
];

export const BOARD_HISTORY = [];

export const UPCOMING_EVENTS = [];

export const PAST_EVENTS = [
  {
    date: "April 24, 2026",
    title: "Picnic Party!",
    host: "BridgeColumbia",
    speakers: "Club social",
    desc:
      "Our final event of the year — an outdoor picnic on the Math Lawns to close out the semester with the coalition.",
    time: "3:00 – 5:00 PM",
    location: "Math Lawns",
  },
  {
    date: "April 21, 2026",
    title: "Do Billionaires Have a Responsibility to Give Away Most of Their Wealth?",
    host: "BridgeColumbia",
    speakers: "Open discussion",
    desc:
      "A discussion on wealth, philanthropy, and moral obligation — do the ultra-wealthy owe society most of their fortunes, or is that the wrong question entirely?",
    time: "8:30 PM",
    location: "Hamilton 511",
  },
  {
    date: "April 15, 2026",
    title: "CU Dems Debate CU Republicans",
    host: "BridgeColumbia × CU Democrats × CU Republicans",
    speakers: "Cross-aisle student debate",
    desc:
      "A high-profile campus debate between Columbia University Democrats and Republicans covering immigration, healthcare, foreign policy, the economy, and the federal bureaucracy.",
    time: "8:00 PM",
    location: "Pupin 301",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/ldla6rqw_IMG_9568.jpg",
  },
  {
    date: "April 14, 2026",
    title: "The Future of Public Health",
    host: "BridgeColumbia · Expert Panel",
    speakers: "Alexander Preker · Paul Wilson · Safura Abdool-Karim",
    desc:
      "Leading experts on global health innovation, the UN Millennium Project, and the Africa Health and Human Rights Network in conversation on the future of public health.",
    time: "8:30 – 9:30 PM",
    location: "Pupin 329",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/652k93uq_IMG_9569.jpg",
  },
  {
    date: "April 7, 2026",
    title: "Can We Separate the Art from the Artist?",
    host: "BridgeColumbia",
    speakers: "Open discussion",
    desc:
      "In response to Kanye West's new album — can we separate the art from the artist? A conversation on aesthetics, ethics, and where fans should (and shouldn't) draw the line.",
    time: "8:30 PM",
    location: "Hamilton 309",
  },
  {
    date: "March 28 – 29, 2026",
    title: "Bridge the Northeast",
    host: "BridgeColumbia · Bridge chapters across the Northeast",
    speakers: "Columbia · NYU · Rutgers · UConn · Dartmouth · StonyBrook · UNH",
    desc:
      "Our multi-day regional summit uniting Bridge chapters across the Northeast — offsite social picnic, political minigames, and a Jubilee-inspired political spectrum activity.",
    time: "Multi-day",
    location: "Riverside Park + Earl Hall Auditorium",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/cd9xaosr_IMG_9563.jpg",
  },
  {
    date: "March 27, 2026",
    title: "The War in Iran: Justified Deterrence or Unnecessary Intervention?",
    host: "BridgeColumbia × The David Network",
    speakers: "Open discussion",
    desc:
      "A cross-organization discussion with The David Network on the war in Iran — justified deterrence, unnecessary intervention, or something in between?",
    time: "Evening",
    location: "Columbia University",
  },
  {
    date: "March 10, 2026",
    title: "A Path Forward? Exploring USA–Iran Dynamics",
    host: "BridgeColumbia",
    speakers: "Moderated small-group discussion",
    desc:
      "A discussion event unpacking the history, current stakes, and possible diplomatic paths between the United States and Iran.",
    time: "8:30 – 9:30 PM",
    location: "Hamilton 511",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/k47cytrg_IMG_1185.webp",
  },
  {
    date: "March 3, 2026",
    title: "Weight in a Polarized Culture: Medical Issue vs. Social Issue?",
    host: "BridgeColumbia",
    speakers: "Open discussion",
    desc:
      "A discussion on weight in America — is it a medical issue, a social issue, or both? A conversation about the science, culture, and politics behind one of the most personal debates in public life.",
    time: "8:30 PM",
    location: "Hamilton 511",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/a0pzjgmx_IMG_1184.webp",
  },
  {
    date: "February 17, 2026",
    title: "Law on Trial: The Issues Defining Gen-Z",
    host: "BridgeColumbia × CU Undergraduate Law Review × Black Pre-Law Society × Athena Pre-Law Society × ColumbiaVotes",
    speakers:
      "Harvey Pennington (moderator) · Zoë Berry · Brianna Wilson · Andrew Chung · Nyrah Siddiqui · Kaixi Catherine Zhang",
    desc:
      "A cross-organization panel unpacking the legal questions shaping our generation, from campus speech to civil rights and the future of the courts.",
    time: "8:30 PM",
    location: "Schermerhorn 614",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/83hrlrua_IMG_9565.jpg",
  },
  {
    date: "February 3, 2026",
    title: "How Should We Respond to What's Going On in Minnesota?",
    host: "BridgeColumbia",
    speakers: "Open discussion",
    desc:
      "A discussion on the ICE raids and immigration enforcement unfolding in Minnesota — how students across the political spectrum are reading the moment, and what a constructive response looks like on campus.",
    time: "8:30 PM",
    location: "Hamilton 115",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/tacirsix_IMG_1183.webp",
  },
  {
    date: "December 2, 2025",
    title: "AI and Public Policy: Threat or Opportunity?",
    host: "BridgeColumbia · Expert Panel",
    speakers:
      "Kathleen McKeown · Yamil Velez · Eduardo Albrecht · Katja Vogt",
    desc:
      "Our final event of the semester — four expert panelists on the future of AI and public policy. Featuring the former Vice Dean of SEAS and founding director of the Data Science Institute, an Assistant Professor of Political Science, a former Senior Fellow at the UN University's Centre for Policy Research, and a Professor of Philosophy at Columbia.",
    time: "8:30 PM EST",
    location: "Earl Hall Auditorium",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/f40a8cts_IMG_1182.webp",
  },
  {
    date: "November 18, 2025",
    title: "Will Things Actually Change?",
    host: "BridgeColumbia",
    speakers: "Moderated discussion",
    desc:
      "A discussion on the Israel–Palestine conflict and the Gaza war — where students on different sides of the issue think things are actually heading, and what campus dialogue can and can't change.",
    time: "Evening",
    location: "Columbia University",
  },
  {
    date: "November 11, 2025",
    title: "The Era of Zohran: Sunrise or Twilight in NYC?",
    host: "BridgeColumbia",
    speakers: "Open discussion",
    desc:
      "A post-election discussion on Zohran Mamdani's win as the new Mayor of NYC — sunrise or twilight? Free food and drink provided.",
    time: "8:30 – 9:30 PM",
    location: "Hamilton 303",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/5nrrp9oh_IMG_1181.webp",
  },
  {
    date: "October 29, 2025",
    title: "Golden Age or Hellscape? Is the New Right Good for Our Country?",
    host: "Braver Angels / BUSA · in collaboration with BridgeColumbia",
    speakers: "Braver Angels Debate · references Charlie Kirk",
    desc:
      "A Braver Angels-style debate on the New Right — is the movement represented by figures like Charlie Kirk good for the country, or a warning sign? Hosted with Braver USA in collaboration with BridgeColumbia.",
    time: "7:00 PM",
    location: "Earl Hall, Dodge Room",
  },
  {
    date: "October 14, 2025",
    title: "Reflection on October 7th, the Question of Genocide, & the Hope of Peace",
    host: "BridgeColumbia",
    speakers: "Moderated reflection",
    desc:
      "A reflection two years after October 7th — the question of genocide, the human cost of the war in Gaza, and where any hope of peace might come from.",
    time: "Evening",
    location: "Hamilton 302",
  },
  {
    date: "September 30, 2025",
    title: "The Rise of Red Pill Men",
    host: "BridgeColumbia",
    speakers: "Moderated discussion",
    desc:
      "A discussion event exploring the online \u201cred pill\u201d movement, why it appeals to young men, and how campuses should respond to shifts in gender politics.",
    time: "Evening",
    location: "Columbia University",
  },
  {
    date: "September 23, 2025",
    title: "Gender Wars: The Politicization of Family and Modern Feminism",
    host: "BridgeColumbia",
    speakers: "Open discussion",
    desc:
      "A weekly discussion on gender politics — how family, feminism, and identity have become political flashpoints, and how students on all sides can talk about it honestly.",
    time: "Evening",
    location: "Hamilton 302",
  },
  {
    date: "September 16, 2025",
    title: "A Discussion on Death and Political Violence",
    host: "BridgeColumbia",
    speakers: "Open discussion · references Charlie Kirk",
    desc:
      "A weekly discussion on death and political violence in the wake of the Charlie Kirk shooting — how a polarized country grieves, argues, and moves forward.",
    time: "Evening",
    location: "Butler Lawn",
  },
  {
    date: "March 6, 2025",
    title: "HXA x BridgeColumbia Panel with Nadine Strossen",
    host: "Heterodox Academy · BridgeColumbia · MilVets · VFLI",
    speakers: "Nadine Strossen — former ACLU President",
    desc:
      "Heterodox Academy, BridgeColumbia, MilVets, and VFLI in conversation with former ACLU president and renowned civil liberties activist Nadine Strossen.",
    time: "Evening",
    location: "Columbia University",
  },
  {
    date: "February 28, 2025",
    title: "BridgeColumbia Outings: Dave & Buster’s",
    host: "BridgeColumbia",
    speakers: "Club social",
    desc:
      "A social outing at Dave & Buster's to bridge the divide off-hours and form club connections.",
    time: "Evening",
    location: "Dave & Buster’s, NYC",
  },
  {
    date: "February 27, 2025",
    title: "Discussion: Transgender Athletes",
    host: "BridgeColumbia",
    speakers: "Small-group moderated dialogue",
    desc:
      "A small-group discussion on how to balance inclusion and fairness when it comes to transgender athletes.",
    time: "Evening",
    location: "Columbia University",
  },
  {
    date: "November 21, 2024",
    title: "BridgeColumbia x CDDA: Braver Campus Dialogue",
    host: "College Debates and Discourse Alliance",
    speakers: "Peter T. Coleman · Manu Meel (BridgeUSA)",
    desc:
      "A Braver Campus Dialogue at Columbia University on the most impactful and productive ways for our community to engage with the Israel–Palestine conflict, featuring social psychologist Peter T. Coleman and BridgeUSA's Manu Meel.",
    time: "5:00 – 6:30 PM",
    location: "Earl Hall Auditorium",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/8rpnm9dx_IMG_1174.jpeg",
  },
  {
    date: "November 7, 2024",
    title: "Pumpkin Spice and Politics",
    host: "Columbia Political Union × BridgeColumbia",
    speakers: "Jubilee-inspired \u201cMiddle Ground\u201d election event",
    desc:
      "An election-season Middle Ground event co-hosted with the Columbia Political Union — Jubilee-inspired format, open to the entire Columbia community. Hungarian Pastry and fall treats provided.",
    time: "5:30 – 6:30 PM",
    location: "Mudd 829",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/y12i3l7s_IMG_1173.jpeg",
  },
  {
    date: "October 31, 2024",
    title: "Listening Tables",
    host: "BridgeColumbia × The Trust Collaboratory",
    speakers: "Small-group reflection session",
    desc:
      "A BridgeColumbia-hosted Listening Tables session in conjunction with the Trust Collaboratory — a space to reflect and openly discuss experiences on campus over the past two semesters.",
    time: "5:00 – 6:00 PM",
    location: "Earl Hall",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_cross-columbia/artifacts/vohp950o_IMG_1175.jpeg",
  },
  {
    date: "October 23, 2024",
    title: "CU Democrats x CU Republicans Discussion",
    host: "BridgeColumbia (moderator)",
    speakers: "Columbia University Democrats & Republicans",
    desc:
      "Columbia University Democrats and Republicans in a BridgeColumbia-moderated discussion across the political aisle.",
    time: "Evening",
    location: "Columbia University",
  },
];

export const MEMBER_BENEFITS = [
  "Lead Bridge discussions and moderate campus dialogue",
  "Private networking events with alumni and civic-dialogue leaders",
  "Private dinners with faculty, speakers, and Bridge guests",
  "Private speaker events reserved for Fellows",
  "1:1 mentorship from board members and our faculty advisor",
  "Priority consideration for board roles \u2014 board members attend the annual BridgeUSA National Summit",
];

export const CAFC_PARTNERSHIP = {
  title: "In partnership with the Columbia Academic Freedom Council",
  body:
    "BridgeColumbia Fellows also get exclusive access to programming from the Columbia Academic Freedom Council (CAFC) \u2014 a faculty-led coalition defending open inquiry and academic freedom at Columbia.",
  perks: [
    "Invitations to CAFC faculty dinners and speaker series",
    "Introductions to CAFC-affiliated faculty across departments",
    "Access to joint BridgeColumbia \u00d7 CAFC events not open to the public",
    "First look at CAFC-hosted seminars, panels, and workshops",
  ],
};

export const CAFC_PAST_GUESTS = [
  {
    name: "President Mnookin",
    role: "President, Columbia University",
    note: "Private dinner hosted through the CAFC partnership.",
  },
  {
    name: "Nadine Strossen",
    role: "Former President, ACLU",
    note: "Featured at CAFC's \u201cHonoring Dissent\u201d weekend conference at The Yale Club of New York City.",
    url: "https://www.sundial-cu.org/p/honoring-dissent-sundials-weekend",
  },
];

export const CONTACT_DETAILS = [
  { label: "Instagram", value: "@bridge.columbia", href: "https://instagram.com/bridge.columbia" },
  { label: "Email", value: "bridgecolumbia@columbia.edu", href: "mailto:bridgecolumbia@columbia.edu" },
  { label: "Meeting Time", value: "Time TBA", href: null },
  { label: "Meeting Location", value: "Location TBA", href: null },
];

// -------- Added for the polished redesign --------

export const STATS = [
  { value: 70, suffix: "+", label: "Events hosted", sub: "Since launching in September 2024" },
  { value: 2000, suffix: "+", label: "Students reached", sub: "At Columbia & Barnard" },
  { value: 4, suffix: "", label: "Discussion norms", sub: "That guide every dialogue", href: "/about#norms" },
  { value: 2024, suffix: "", label: "Launched", sub: "September \u00b7 Columbia University" },
];

export const COLLABORATORS = [
  "BridgeUSA",
  "Heterodox Academy",
  "CDDA",
  "MilVets",
  "VFLI",
  "CU Democrats",
  "CU Republicans",
  "The Columbia Sundial",
  "Peter T. Coleman, PhD",
];

export const FEATURED_QUOTE = {
  quote:
    "In a moment defined by polarization, students who learn to disagree well are students who can lead. BridgeColumbia gives them that training ground.",
  author: "Peter T. Coleman, PhD",
  role:
    "Professor of Psychology & Education, Columbia University \u00b7 Faculty Mentor",
};

export const NAV_DESCRIPTIONS = {
  Home: "The Columbia chapter of BridgeUSA.",
  About: "Mission, history, and the Bridge mindset.",
  Board: "Meet the student leaders.",
  Events: "Discussions, debates, speakers \u2014 upcoming and past.",
  "Get Involved": "Become a member, join the listserv, contact us.",
  Constitution: "Policies that govern the chapter.",
};

export const COMMITTEES = [
  {
    name: "Communications Committee",
    tagline: "Marketing \u00b7 Content \u00b7 Storytelling",
    body:
      "Our new committee owns BridgeColumbia\u2019s public face \u2014 street interviews, event coverage, social clips, and everything that helps the campus find us. We\u2019re opening two role tracks in the same committee:",
    roles: [
      {
        title: "Street Interviewer",
        icon: "mic",
        body:
          "Take the mic to campus. Approach students, ask smart questions, and capture candid takes on the topics we cover. You bring energy, curiosity, and comfort in front of a camera.",
        skills: ["On-camera presence", "Quick thinking", "Curiosity"],
      },
      {
        title: "Video Editor",
        icon: "film",
        body:
          "Turn raw street-interview footage into short-form social pieces \u2014 IG reels, TikToks, and highlight clips. You know your way around a timeline, captions, and pacing.",
        skills: ["Premiere / CapCut / DaVinci", "Motion + captions", "Sound design basics"],
      },
    ],
    applyUrl: null, // Uses BRAND.committeeUrl (mailto) — see GetInvolved.jsx
  },
];


export const FAQS = [
  {
    q: "Do I need to be politically engaged to apply for the fellowship?",
    a: "Not at all. BridgeColumbia welcomes students from all backgrounds \u2014 including those who feel politically homeless or disengaged. What matters is a willingness to listen and engage with people who see things differently.",
  },
  {
    q: "Is BridgeColumbia affiliated with a political party?",
    a: "No. We are explicitly multi-partisan. We do not endorse candidates, parties, or partisan positions. Our programming is designed to create space for all viewpoints, not to advance any particular one.",
  },
  {
    q: "What happens at a typical discussion?",
    a: "A trained moderator poses a question and guides conversation according to our four discussion norms. Groups are small (usually 8\u201312 students) to ensure every voice can be heard.",
  },
  {
    q: "How much of a time commitment is the fellowship?",
    a: "Fellows commit to attending most Bridge events during the year plus a light programming or planning role. Beyond that, it's as involved as you want it to be \u2014 CAFC dinners, private speakers, and 1:1 mentorship are all opt-in. Note: the annual BridgeUSA National Summit is reserved for board members, and Fellows get priority when board roles open up.",
  },
  {
    q: "Can my student organization co-host an event?",
    a: "Yes, and we encourage it. Some of our best programming has come from collaborations with Heterodox Academy, MilVets, VFLI, CU Dems, and CU Republicans. Email us to start a conversation.",
  },
];
